import React from 'react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/utils';

interface AuthSubmitButtonProps {
    children: React.ReactNode;
    /** Swaps the label for a spinner and blocks the click. */
    loading?: boolean;
    disabled?: boolean;
    type?: 'submit' | 'button';
    onClick?: () => void;
    className?: string;
}

/**
 * The lead action on an auth card: sign in, create account, send reset link,
 * log in from the conflict view. One component because those four were four
 * copies of the same `variant="primary" size="lg" className="w-full
 * font-semibold"` string, which is how they would have drifted apart.
 *
 * NOT `variant="primary"`. In Ceramic Night that fill is bone at 81.6%
 * lightness and the auth card interior sits near 13%, so the button was the
 * brightest object on a surface whose entire vocabulary is bone hairlines and a
 * bone text ramp over glass, brighter even than the wordmark. Owner call
 * 2026-09-20: too loud for this surface.
 *
 * Plain `secondary`, with nothing layered on top. That is the same control the
 * Google button below it already is, so the two full-width actions on the card
 * finally match, and it is dark: surface over a dark card, one hairline, which
 * is the register the whole screen is in. It leads on position and on weight,
 * not on brightness.
 *
 * h-11, not `size="lg"`'s h-10: every other full-width control on these cards
 * (the inputs, the Google button) is h-11, and the submit sitting 4px shorter
 * than the button directly under it was visible.
 */
export const AuthSubmitButton: React.FC<AuthSubmitButtonProps> = ({
    children,
    loading = false,
    disabled = false,
    type = 'submit',
    onClick,
    className,
}) => (
    <Button
        type={type}
        variant="secondary"
        onClick={onClick}
        disabled={disabled || loading}
        className={cn(
            'w-full h-11 font-semibold',
            className,
        )}
    >
        {loading ? <Spinner /> : children}
    </Button>
);
